/**
 * Which tenant a request is addressed to, and which names may never be one.
 *
 * An environment is identified by a **base domain**. When one is configured the
 * host is authoritative (`chevron.socratiq.ie` addresses Chevron); when it is
 * not, the first path segment is (`localhost:5173/chevron`). The two are
 * mutually exclusive by construction, so `chevron.socratiq.ie/dbs` can never be
 * ambiguous: with a base domain set, a path segment is never read as a tenant.
 *
 * That exclusivity is the point. An ambiguity resolved silently is how a caller
 * ends up acting in a tenant they did not think they were in, and a precedence
 * rule is only as good as the next person to read it.
 *
 * Neither mode grants anything. Both produce a slug, which the caller resolves
 * to a tenant id and hands to `selectTenant`, where it is still checked against
 * the caller's memberships. A forged host or path buys nothing.
 *
 * @module server/tenant-addressing
 */

/**
 * Labels that can never be a tenant slug.
 *
 * The set is the union of both addressing modes, because a tenant created under
 * one mode outlives the environment it was created in.
 *
 * Path mode is the stricter half: `api`, `health` and `assets` are live server
 * paths, so a tenant slugged `api` would not merely shadow a name, it would sit
 * on top of the API and break the application. Host mode contributes the names
 * owned by something else on the domain: the brochure and docs site, the
 * sign-in origin, and every environment label, since `dev.socratiq.ie` must not
 * become addressable as a tenant of production.
 */
export const RESERVED_TENANT_SLUGS: ReadonlySet<string> = new Set([
  // Live server paths. These break the application, not just the name.
  "api",
  "assets",
  "health",
  // Owned by the brochure and docs site.
  "www",
  "docs",
  "help",
  // The sign-in origin, which must resolve to no tenant at all.
  "login",
  // prod's platform entry point (infra/main.bicep binds it there instead of
  // the true apex, which a separate site owns), must resolve to no tenant.
  "app",
  // Top-level client-side route segments. A tenant slugged the same as one of
  // these would make its own route unreachable under path addressing, since
  // the router's basename would swallow the segment as a tenant instead of
  // matching it as a route.
  "admin",
  "portfolio",
  "reports",
  "programmes",
  "modules",
  "programme-teams",
  "access-denied",
  // Environment labels, current and reserved for later.
  "dev",
  "ppe",
  "prod",
  "staging",
  "test",
]);

/**
 * Whether a slug is reserved for infrastructure.
 *
 * @param slug - Candidate slug, already normalised to lowercase.
 * @returns `true` when the slug may not be assigned to a tenant.
 */
export function isReservedTenantSlug(slug: string): boolean {
  return RESERVED_TENANT_SLUGS.has(slug);
}

/** Shape a slug has to have to be addressable, matching `SLUG_PATTERN`. */
const SLUG_SHAPE = /^[a-z0-9-]+$/;

/**
 * Strip the port from a `Host` header value and normalise it.
 *
 * The header carries `host[:port]`, and an IPv6 literal is bracketed, so the
 * port is only the part after the last colon that follows a closing bracket or
 * contains no colon at all.
 *
 * @param host - Raw `Host` header value.
 * @returns Lowercased hostname with no port, or `undefined` when absent.
 */
function hostname(host: string | undefined): string | undefined {
  const trimmed = host?.trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith("[")) {
    return trimmed.slice(0, trimmed.indexOf("]") + 1) || undefined;
  }
  const colon = trimmed.lastIndexOf(":");
  return (colon === -1 ? trimmed : trimmed.slice(0, colon)) || undefined;
}

/**
 * The tenant label a host addresses, under a given base domain.
 *
 * Returns nothing for anything that is not exactly one label under the base
 * domain: the bare environment host, a deeper name, the Container Apps FQDN,
 * `localhost`, and every reserved label. Falling back rather than failing is
 * deliberate. The sign-in origin, the health probe and the Traffic Manager
 * monitor all arrive on hosts that address no tenant, and a hard failure here
 * would take all three down at once.
 *
 * @param host - Raw `Host` header value.
 * @param baseDomain - The environment's base domain, e.g. `socratiq.ie`.
 * @returns The tenant slug, or `undefined` when the host addresses none.
 */
export function tenantSlugFromHost(
  host: string | undefined,
  baseDomain: string,
): string | undefined {
  const name = hostname(host);
  const base = baseDomain
    .trim()
    .toLowerCase()
    .replace(/^\.+|\.+$/g, "");
  if (!name || !base || name === base) {
    return undefined;
  }

  const suffix = `.${base}`;
  if (!name.endsWith(suffix)) {
    return undefined;
  }

  // Matched as a literal suffix, never parsed. `dev.socratiq.ie` is a
  // subdomain rather than a registrable domain, so anything that reasons about
  // "the last two labels" is wrong for at least one environment.
  const label = name.slice(0, -suffix.length);
  if (!SLUG_SHAPE.test(label) || isReservedTenantSlug(label)) {
    return undefined;
  }
  return label;
}

/**
 * The tenant label a browser location path addresses.
 *
 * This runs in the **browser**, not on the server. With no base domain the SPA
 * is served at `/<tenant>` and reads its own prefix with this, then sends the
 * result as `x-platform-tenant`.
 *
 * The server deliberately does not read its own request path for this. The API
 * is mounted at `/api/v1`, outside any tenant segment, so every API request's
 * first segment is the reserved label `api` and a server-side path lookup would
 * resolve nothing while looking as though it worked.
 *
 * @param path - Browser location path, with or without a query string.
 * @returns The tenant slug, or `undefined` when the path addresses none.
 */
export function tenantSlugFromPath(path: string): string | undefined {
  const segment = path.split("?")[0].split("/").filter(Boolean)[0]?.toLowerCase();
  if (!segment || !SLUG_SHAPE.test(segment) || isReservedTenantSlug(segment)) {
    return undefined;
  }
  return segment;
}

/**
 * The tenant a request is addressed to, from the server's point of view.
 *
 * With a base domain the host decides, and it decides alone: a path segment is
 * never consulted, so `chevron.socratiq.ie/dbs` cannot be ambiguous, and a host
 * that names no tenant does not fall through to the path either.
 *
 * Without one this returns nothing, and the caller falls back to the
 * `x-platform-tenant` header, which is what the SPA fills in from its own path
 * prefix. Both routes end at the same `selectTenant`, so the tenant is still
 * checked against the caller's memberships either way and neither grants
 * anything on its own.
 *
 * @param request - The request's `Host` header.
 * @param baseDomain - The environment's base domain, or `undefined` when
 *   tenants are addressed by path instead.
 * @returns The tenant slug the host addresses, if any.
 */
export function tenantSlugForRequest(
  request: { host?: string },
  baseDomain: string | undefined,
): string | undefined {
  const base = baseDomain?.trim();
  return base ? tenantSlugFromHost(request.host, base) : undefined;
}
